import { apiVersion } from '../apis.const';
import { transformResponse } from '../apis.util';
import { baseApi } from '../base.api';

/**
 * API Urls
 */
const employeeUrl = (orgSlug: string) => `/${orgSlug}/${apiVersion}/employee`;
const employeeIdUrl = ({ orgSlug, id }: { orgSlug: string; id: number }) => `/${orgSlug}/${apiVersion}/employee/${id}`;
const employeeCodeUrl = (orgSlug: string) => `/${orgSlug}/${apiVersion}/employee/code`;

/**
 * Create APIs
 */
export const employeesApi = baseApi.injectEndpoints({
  overrideExisting: false,

  endpoints: (build) => ({
    fetchEmployees: build.query<FetchEmployeesRes, FetchEmployeesReq>({
      query: ({ orgSlug }) => ({
        url: employeeUrl(orgSlug),
      }),
      transformResponse,
      providesTags: ['Employees'],
    }),

    fetchEmployee: build.query<FetchEmployeeRes, FetchEmployeeReq>({
      query: ({ orgSlug, id }) => ({
        url: employeeIdUrl({ orgSlug, id }),
      }),
      transformResponse,
    }),

    createEmployee: build.mutation<CreateEmployeeRes, CreateEmployeeReq>({
      query: ({ orgSlug, employee }) => ({
        url: employeeUrl(orgSlug),
        method: 'POST',
        body: employee,
      }),
      transformResponse,
      invalidatesTags: ['Employees'],
    }),

    updateEmployee: build.mutation<UpdateEmployeeRes, UpdateEmployeeReq>({
      query: ({ orgSlug, id, employee }) => ({
        url: employeeIdUrl({ orgSlug, id }),
        method: 'PUT',
        body: employee,
      }),
      transformResponse,
      invalidatesTags: ['Employees'],
    }),

    deleteEmployee: build.mutation<DeleteEmployeeRes, DeleteEmployeeReq>({
      query: ({ orgSlug, id }) => ({
        url: employeeIdUrl({ orgSlug, id }),
        method: 'DELETE',
      }),
      invalidatesTags: ['Employees'],
    }),

    generateEmployeeCode: build.query<GenerateEmployeeCodeRes, GenerateEmployeeCodeReq>({
      query: ({ orgSlug }) => ({
        url: employeeCodeUrl(orgSlug),
      }),
      transformResponse,
    }),
  }),
});

/**
 * Hooks
 */
export const {
  useFetchEmployeesQuery,
  useFetchEmployeeQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
  useDeleteEmployeeMutation,
  useGenerateEmployeeCodeQuery,
} = employeesApi;

/**
 * Types
 */
export type FetchEmployeesRes = any;
export type FetchEmployeesReq = {
  orgSlug: string;
};
export type FetchEmployeeRes = any;
export type FetchEmployeeReq = {
  orgSlug: string;
  id: number;
};
export type CreateEmployeeRes = any;
export type CreateEmployeeReq = {
  orgSlug: string;
  employee: {
    name: string;
    code: string | null;
    email: string;
    phone: string;
  };
};
export type UpdateEmployeeRes = any;
export type UpdateEmployeeReq = {
  orgSlug: string;
  id: number;
  employee: {
    name: string;
    code: string | null;
    email: string;
    phone: string;
  };
};
export type UpdateEmployeeReqBody = {
  name: string;
  code: string | null;
  email: string;
  phone: string;
};
export type DeleteEmployeeRes = any;
export type DeleteEmployeeReq = {
  orgSlug: string;
  id: number;
};
export type GenerateEmployeeCodeRes = any;
export type GenerateEmployeeCodeReq = {
  orgSlug: string;
};
